import React from "react";
import { Grid2, Stack, Typography } from "@mui/material";
import { GroupAdd } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";

type P = {
  schedule: BookingScheduleInterface;
  isMobile: boolean;
};

/** Conference schedules may list invited visit-only visitors via {@link BookingScheduleInterface.visitorBookings}. */
function shouldShowConferenceVisitorList(schedule: BookingScheduleInterface): boolean {
  if (schedule.bookingType !== BookingType.CONFERENCEZONE) {
    return false;
  }
  return (schedule.visitorBookings?.length ?? 0) > 0;
}

export const ScheduleEventExternalUser: React.FC<P> = ({ schedule, isMobile }) => {
  const { t } = useTranslation();
  const showConferenceVisitors = shouldShowConferenceVisitorList(schedule);

  return (
    <Grid2
      sx={[
        isMobile ? { borderLeft: "none" } : { borderLeft: "inset" },
        isMobile ? { pl: 0 } : { pl: 3 }
      ]}
    >
      <Grid2 display={"flex"} alignItems={"center"} sx={[isMobile ? { mb: 1 } : { mb: 2, mt: 1 }]}>
        <GroupAdd style={{ marginRight: "1rem" }} fontSize="small" />
        <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
          {t("Visitors")}
        </Typography>
      </Grid2>
      {showConferenceVisitors ? (
        <Stack
          spacing={1}
          data-testid="schedule-item-conference-visitors"
          divider={<Grid2 sx={{ borderTop: theme => `1px dashed ${theme.palette.divider}` }} />}
        >
          {(schedule.visitorBookings ?? []).map((visitor, index) => {
            const name = `${visitor.firstName ?? ""} ${visitor.surname ?? ""}`.trim();
            const key = visitor.email ?? `${name}-${index}`;
            return (
              <Stack key={key} data-testid={`schedule-item-conference-visitor-${index}`}>
                <Typography style={{ fontWeight: "bold" }}>
                  {name || t("_visitorFirstNameOnly")}
                </Typography>
                {visitor.company && <Typography>{visitor.company}</Typography>}
                {visitor.email && <Typography>{visitor.email}</Typography>}
                {visitor.locationName && (
                  <Typography variant="body2" color="text.secondary">
                    {visitor.locationName}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <>
          <Typography data-testid="schedule-item-external" style={{ fontWeight: "bold" }}>
            {schedule?.externalName}
          </Typography>
          <Typography>{schedule?.externalCompany}</Typography>
          <Typography>{schedule?.externalEmail}</Typography>
        </>
      )}
    </Grid2>
  );
};
